import React, { Component } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import MathleticsLogo from '@3plearning/3p-teacheradmin-assets/assets/svg/Mathletics.svg';
import Isvg from 'react-inlinesvg';
import { cloneDeep } from 'lodash';
import css from './participation-report.scss';
import UrlHelper from '../../helpers/url-helper';
import participationReportActions, { PARTICIPATION_REPORT_RETRIEVED } from './participation-report-actions';
import modalActions from '../../components/modal/modal-actions';
import { formatDate } from '../../helpers/utils';
import {
    ACTIVITIES_POINTS_INFO,
    ACTIVITIES_TABLE_CONFIG,
    LIVE_MATHS_HEADER,
    LIVE_MATHS_INFO,
    LIVE_MATHS_TABLE_CONFIG,
    NEW_COURSES_TABLE_CONFIG,
    NEW_COURSES_POINTS_INFO,
    CHALLENGES_POINTS_INFO,
    CHALLENGES_TABLE_CONFIG,
    SKILL_QUEST_POINTS_INFO,
    SKILL_QUEST_TABLE_CONFIG,
    WEEKLY_POINTS,
    WEEKLY_POINTS_INFO
} from './participation-report-constants';
import Certificates from '../../components/certificates/certificates';
import LivePoints from '../../components/live-points/live-points';
import LoginInfo from '../../components/login-info/login-info';
import ReportTable from '../../components/report-table/report-table';
import localeActions from '../../locale/locale-actions';
import { locale, translateTableLabels } from '../../locale/locale-utils';
import RankTiles from './sub-components/rank-tiles';

/**
 * ParticipationReport Redux component
 * Receives properties via 'props'
 * Displays a participation report for a student
 */
export class ParticipationReport extends Component {
    static propTypes = {
        participationReport: PropTypes.object,
        getParticipationReport: PropTypes.func,
        localeData: PropTypes.object,
        setTranslationData: PropTypes.func,
        openModal: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.state = {
            activitiesFooterData: {}, // each course will add a new entry into object, using the course name as the key
            newCoursesFooterData: {},
            skillQuestFooterData: {},
            challengesFooterData: null,
            liveMathsFooterData: null,
            tableConfigActivities: {},
            tableConfigLiveMaths: {},
            tableConfigSkillQuest: {},
            tableConfigNewCourses: {},
            tableConfigChallenges: {},
            studentName: '',
            formattedStartDate: '',
            formattedEndDate: '',
            academicYear: '',
            locale: ''
        };

        // this page gets a lot of its data from query paramaters as it is opened in a new tab
        const queryParams = [
            'studentName',
            'profileId',
            'productId',
            'schoolId',
            'classroomId',
            'startDate',
            'endDate',
            'academicYear',
            'locale'
        ];
        queryParams.forEach((paramName) => {
            this.state[paramName] = UrlHelper.getQueryParamValue(paramName);
        });
        /**
         * SET LOCALE
         */
        const localeCode = locale() || 'en-AU';
        // eslint-disable-next-line global-require,import/no-dynamic-require
        require(`dayjs/locale/${localeCode.toLowerCase()}`);
        dayjs.locale(localeCode);
        this.state.formattedStartDate = formatDate(this.state.startDate);
        this.state.formattedEndDate = formatDate(this.state.endDate);
    }

    componentDidMount() {
        const { profileId, productId, schoolId, classroomId, startDate, endDate, academicYear } = this.state;
        const { setTranslationData } = this.props;
        this.props.getParticipationReport({
            productId, profileId, schoolId, classroomId, startDate, endDate, academicYear
        }).then(
            (action) => {
                if (action.type !== PARTICIPATION_REPORT_RETRIEVED) {
                    this.displayError(action.error);
                }
            },
            error => this.displayError(error.message)
        );
        setTranslationData();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.participationReport) {
            const {
                courseResult,
                liveMathsResult,
                questCourseResult,
                newCoursesResult,
                challengesResult
            } = nextProps.participationReport;


            this.generateFooterData({
                courseResult,
                liveMathsResult,
                questCourseResult,
                newCoursesResult,
                challengesResult
            });
            this.setTablesConfig();
        }
    }

    setTablesConfig = () => {
        const tableConfigActivities = cloneDeep(ACTIVITIES_TABLE_CONFIG);
        const tableConfigLiveMaths = cloneDeep(LIVE_MATHS_TABLE_CONFIG);
        const tableConfigSkillQuest = cloneDeep(SKILL_QUEST_TABLE_CONFIG);
        const tableConfigNewCourses = cloneDeep(NEW_COURSES_TABLE_CONFIG);
        const tableConfigChallenges = cloneDeep(CHALLENGES_TABLE_CONFIG);
        const { localeData } = this.props;
        translateTableLabels(tableConfigActivities, localeData);
        translateTableLabels(tableConfigLiveMaths, localeData);
        translateTableLabels(tableConfigSkillQuest, localeData);
        translateTableLabels(tableConfigNewCourses, localeData);
        translateTableLabels(tableConfigChallenges, localeData);
        this.setState({
            tableConfigActivities,
            tableConfigLiveMaths,
            tableConfigSkillQuest,
            tableConfigNewCourses,
            tableConfigChallenges
        });
    };

    generateFooterData({ courseResult, liveMathsResult, questCourseResult, newCoursesResult, challengesResult }) {
        const skillQuestFooterData = {};
        questCourseResult.forEach((course) => {
            const { averageAccuracy } = course;
            skillQuestFooterData[course.courseName] = { averageAccuracy };
        });
        const activitiesFooterData = {};
        courseResult.courses.forEach((course) => {
            const { averageResult, totalPoints } = course;
            activitiesFooterData[course.courseName] = { averageResult, totalPoints };
        });
        const {
            maxLiveMathsLevels,
            averageCorrect,
            totalPoints
        } = liveMathsResult;
        const newCoursesFooterData = {};
        newCoursesResult.forEach((course) => {
            const { averageResult, totalPoints: totalCurriculumPoints } = course;
            newCoursesFooterData[course.courseName] = { averageResult, totalPoints: totalCurriculumPoints };
        });
        const {
            averageResult, totalPoints: totalChallengesPoints
        } = challengesResult;

        this.setState({
            activitiesFooterData,
            liveMathsFooterData: {
                maxLiveMathsLevels, averageCorrect, totalPoints
            },
            skillQuestFooterData,
            newCoursesFooterData,
            challengesFooterData: {
                averageResult, totalChallengesPoints
            }
        });
    }

    displayError = (error) => {
        const { localeData } = this.props;
        this.props.openModal({
            title: localeData['Modal - Oops'],
            content: <p>{error}</p>,
            buttons: [{ title: localeData['Modal - TryAgain'], primary: true }]
        });
    };

    render() {
        const {
            studentName, formattedStartDate, formattedEndDate,
            activitiesFooterData, liveMathsFooterData, skillQuestFooterData,
            newCoursesFooterData, challengesFooterData,
            tableConfigActivities, tableConfigLiveMaths, tableConfigSkillQuest,
            tableConfigNewCourses, tableConfigChallenges
        } = this.state;
        const { participationReport, localeData } = this.props;
        if (!participationReport) {
            return <div />;
        }
        const {
            newCertificateSummary,
            pastCertificateSummary,
            participationInfo,
            logInInfo,
            liveMathsResult: { liveMathsInfo },
            courseResult,
            questCourseResult,
            newCoursesResult,
            challengesResult: { challenges }
        } = participationReport;

        const skillQuestHeaderPrefix = localeData['TableHeader - SkillQuests'];
        const activitiesHeaderPrefix = localeData['TableHeader - Activities'];
        const newCoursesHeaderPrefix = localeData['TableHeader - NewCourses'];
        const challengesHeaderPrefix = localeData['TableHeader - Challenges'];

        return (
            <div className={css.component}>
                <div className={css.header}>
                    <div className={css.logo}><Isvg src={MathleticsLogo} /></div>
                    <h1>{localeData['Header - ClassDetails']}</h1>
                </div>
                <div className={css['selection-info']}>
                    <h2 className={css['student-name']}>{studentName}</h2>
                    <div className={css['selected-date']}>
                        {`${formattedStartDate} - ${formattedEndDate}`}
                    </div>
                </div>
                {participationReport && (
                    <div>
                        <div className={css.section}>
                            <div className={css.heading}>{localeData['Label - NewCertificates']}</div>
                            <div className={css['new-certificate-info']}>
                                <RankTiles newCertificateSummary={newCertificateSummary} />
                            </div>
                            <div className={css.info}>{localeData[WEEKLY_POINTS]}</div>
                        </div>
                        <div className={css['student-certificate-info']}>
                            <Certificates pastCertificateSummary={pastCertificateSummary} />
                            <LivePoints participationInfo={participationInfo} courseResult={courseResult} />
                            <LoginInfo logInInfo={logInInfo} />
                        </div>
                        <div className={css['weekly-points-info']}>{localeData[WEEKLY_POINTS_INFO]}</div>
                        {courseResult.courses.map(course => (
                            <div className={css.activities} key={course.courseName}>
                                <ReportTable
                                    heading={`${activitiesHeaderPrefix}: ${course.courseName}`}
                                    subHeading={localeData[ACTIVITIES_POINTS_INFO]}
                                    tableConfig={tableConfigActivities}
                                    tableData={course.allActivities}
                                    tableFooterData={activitiesFooterData[course.courseName]}
                                />
                            </div>
                        ))}
                        {newCoursesResult[0] && newCoursesResult.map(result => (
                            <div className={css['new-courses']} key={result.courseName}>
                                <ReportTable
                                    heading={`${newCoursesHeaderPrefix}: ${result.courseName}`}
                                    subHeading={localeData[NEW_COURSES_POINTS_INFO]}
                                    tableConfig={tableConfigNewCourses}
                                    tableData={result.courseResults}
                                    tableFooterData={newCoursesFooterData[result.courseName]}
                                />
                            </div>
                        ))}
                        {challenges.length > 0 &&
                            <div className={css.challenges}>
                                <ReportTable
                                    heading={challengesHeaderPrefix}
                                    subHeading={localeData[CHALLENGES_POINTS_INFO]}
                                    tableConfig={tableConfigChallenges}
                                    tableData={challenges}
                                    tableFooterData={challengesFooterData}
                                />
                            </div>
                        }
                        {questCourseResult.map(result => (
                            <div className={css['skill-quest']} key={result.courseName}>
                                <ReportTable
                                    heading={`${skillQuestHeaderPrefix}: ${result.courseName}`}
                                    subHeading={localeData[SKILL_QUEST_POINTS_INFO]}
                                    tableConfig={tableConfigSkillQuest}
                                    tableData={result.allActivities}
                                    tableFooterData={skillQuestFooterData[result.courseName]}
                                />
                            </div>
                        ))}
                        {liveMathsInfo.length > 0 &&
                            <div className={css['live-maths']}>
                                <ReportTable
                                    heading={localeData[LIVE_MATHS_HEADER]}
                                    subHeading={localeData[LIVE_MATHS_INFO]}
                                    tableConfig={tableConfigLiveMaths}
                                    tableData={liveMathsInfo}
                                    tableFooterData={liveMathsFooterData}
                                />
                            </div>
                        }
                    </div>
                )}
            </div>
        );
    }
}

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from ParticipationReportCombinedReducers.
 */
function mapStateToProps(state) {
    return {
        participationReport: state.participationReport,
        localeData: state.localeData
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getParticipationReport: participationReportActions.getParticipationReport,
        setTranslationData: localeActions.setTranslationData,
        openModal: modalActions.openModal
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(styleable(css)(ParticipationReport));



// WEBPACK FOOTER //
// ./containers/participation-report/participation-report.js